import * as React from "react";
import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";

import { cn } from "../../lib/utils";
import { Button } from "./button";

const alertDialogStyles = {
  trigger: "cursor-pointer",
  overlay:
    "fixed inset-0 z-modal bg-bg-scrim opacity-100 backdrop-blur-sm transition-all duration-200 ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0",
  viewport:
    "fixed inset-0 z-modal grid place-items-center overflow-y-auto p-4 data-[bottom-stick-on-mobile=true]:max-sm:items-end data-[bottom-stick-on-mobile=true]:max-sm:p-0",
  content:
    "relative z-modal flex max-h-full w-full max-w-[480px] origin-center flex-col overflow-hidden rounded-xl border border-border-primary bg-bg-elevated bg-clip-padding text-text-primary shadow-L4-wax outline-none [--dialog-padding-x:20px] [--dialog-padding-y:20px] transition-[scale,opacity,translate] duration-200 ease-in-out will-change-transform data-[nested-dialog-open]:after:absolute data-[nested-dialog-open]:after:inset-0 data-[nested-dialog-open]:after:rounded-[inherit] data-[nested-dialog-open]:after:bg-black/5 data-[nested-dialog-open]:after:content-[''] data-ending-style:opacity-0 data-starting-style:opacity-0 sm:data-ending-style:scale-98 sm:data-starting-style:scale-98 data-[bottom-stick-on-mobile=true]:max-sm:max-w-none data-[bottom-stick-on-mobile=true]:max-sm:origin-bottom data-[bottom-stick-on-mobile=true]:max-sm:rounded-b-none data-[bottom-stick-on-mobile=true]:max-sm:data-ending-style:translate-y-4 data-[bottom-stick-on-mobile=true]:max-sm:data-starting-style:translate-y-4 [&>form:not([data-dialog-form])]:flex [&>form:not([data-dialog-form])]:min-h-0 [&>form:not([data-dialog-form])]:flex-col [&>[data-dialog-section]~[data-dialog-section]]:pt-0",
  close:
    "absolute right-2.5 top-2.5 inline-flex size-5.5 items-center justify-center rounded-md bg-transparent text-text-secondary outline-none hover:bg-tint-secondary hover:text-text-primary data-[hover]:bg-tint-secondary data-[hover]:text-text-primary data-[pressed]:bg-tint-secondary data-[pressed]:text-text-primary aria-pressed:bg-tint-secondary aria-pressed:text-text-primary focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none [&_svg]:size-3.5",
  header:
    "flex flex-col gap-1 px-[var(--dialog-padding-x)] py-[var(--dialog-padding-y)] text-left",
  footer:
    "flex items-center justify-end gap-1.5 px-[var(--dialog-padding-x)] py-[var(--dialog-padding-y)]",
  title: "m-0 text-md font-medium leading-md text-text-primary",
  description: "m-0 text-nav leading-nav text-text-secondary",
  panel:
    "min-h-0 overflow-y-auto px-[var(--dialog-padding-x)] py-[var(--dialog-padding-y)]",
};

export const AlertDialogCreateHandle: typeof AlertDialogPrimitive.createHandle =
  AlertDialogPrimitive.createHandle;

export const AlertDialog: typeof AlertDialogPrimitive.Root =
  AlertDialogPrimitive.Root;

export const AlertDialogPortal: typeof AlertDialogPrimitive.Portal =
  AlertDialogPrimitive.Portal;

export function AlertDialogTrigger(
  props: AlertDialogPrimitive.Trigger.Props,
): React.ReactElement {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

export function AlertDialogBackdrop({
  className,
  ...props
}: AlertDialogPrimitive.Backdrop.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-backdrop"
      className={cn(alertDialogStyles.overlay, className)}
      {...props}
    />
  );
}

export const AlertDialogOverlay = AlertDialogBackdrop;

export function AlertDialogViewport({
  className,
  ...props
}: AlertDialogPrimitive.Viewport.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Viewport
      data-slot="alert-dialog-viewport"
      className={cn(alertDialogStyles.viewport, className)}
      {...props}
    />
  );
}

export function AlertDialogPopup({
  className,
  bottomStickOnMobile = true,
  portalProps,
  ...props
}: AlertDialogPrimitive.Popup.Props & {
  bottomStickOnMobile?: boolean;
  portalProps?: AlertDialogPrimitive.Portal.Props;
}): React.ReactElement {
  return (
    <AlertDialogPortal {...portalProps}>
      <AlertDialogBackdrop />
      <AlertDialogViewport data-bottom-stick-on-mobile={bottomStickOnMobile}>
        <AlertDialogPrimitive.Popup
          data-slot="alert-dialog-popup"
          data-bottom-stick-on-mobile={bottomStickOnMobile}
          className={cn(alertDialogStyles.content, className)}
          {...props}
        />
      </AlertDialogViewport>
    </AlertDialogPortal>
  );
}

export const AlertDialogContent = AlertDialogPopup;

export function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(alertDialogStyles.header, className)}
      {...props}
    />
  );
}

export function AlertDialogFooter({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?: "default" | "bare";
}): React.ReactElement {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        alertDialogStyles.footer,
        variant === "bare" && alertDialogStyles.panel,
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogTitle({
  className,
  ...props
}: AlertDialogPrimitive.Title.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(alertDialogStyles.title, className)}
      {...props}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...props
}: AlertDialogPrimitive.Description.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(alertDialogStyles.description, className)}
      {...props}
    />
  );
}

export function AlertDialogAction({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="alert-dialog-action"
      className={cn(className)}
      {...props}
    />
  );
}

export function AlertDialogCancel({
  className,
  variant = "outline",
  size = "default",
  ...props
}: AlertDialogPrimitive.Close.Props &
  Pick<
    React.ComponentProps<typeof Button>,
    "variant" | "size"
  >): React.ReactElement {
  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-cancel"
      className={cn(className)}
      render={<Button variant={variant} size={size} />}
      {...props}
    />
  );
}

export function AlertDialogClose(
  props: AlertDialogPrimitive.Close.Props,
): React.ReactElement {
  return (
    <AlertDialogPrimitive.Close data-slot="alert-dialog-close" {...props} />
  );
}

export { AlertDialogPrimitive };
